import * as React from "react";
import { connect } from "react-redux";
import { Button } from "react-bootstrap";
import { AppShape, HurdleShape } from "@eave/shared/types/api";
import HurdleView from "underwriter_dashboard/shared/HurdleView";

import Money from "../../Money";

const styles = require("underwriter_dashboard/hurdle_editors/styles.module.scss");

interface HurdleEditorProps {
  dispatch?: Function;
  app: AppShape;
  hurdle: HurdleShape;
  onHide: Function;
}

class SecondLienHurdleEditor extends React.Component<HurdleEditorProps, any> {
  constructor(props) {
    super(props);
    const { app } = this.props;

    const appraisedValue = app.appraisal ? parseFloat(app.appraisal.appraised_value) : 0;
    const maxLoanForAppraisedValue =
      (appraisedValue || parseInt(app.purchased_property_avm, 10)) * 0.8;
    const monthlyDebt = parseFloat(app.decision.monthly_debt);
    const monthlyIncome = parseFloat(app.decision.monthly_income);
    const purchasedPropertyAVM = parseFloat(app.purchased_property_avm);
    const salePrice = parseInt(app.computed_properties.purchase_price, 10);
    const secondLienMonthlyPayment = parseFloat(app.decision.second_lien_monthly_payment);

    const availableFunds = parseFloat(app.decision.available_funds);
    const bigFixAmountNeeded = parseFloat(app.decision.big_fix_shortfall);
    const downPaymentAmount = parseFloat(app.decision.downpayment_estimate);
    const maxSecondLienAmt = parseFloat(app.decision.max_approved_second_lien);
    const closingCosts = parseFloat(app.closing_fees_total);

    const fixable = bigFixAmountNeeded <= maxSecondLienAmt + availableFunds - downPaymentAmount;

    this.state = {
      appraisedValue,
      availableFunds,
      bigFixAmountNeeded,
      downPaymentAmount,
      fixable,
      maxLoanForAppraisedValue,
      maxSecondLienAmt,
      monthlyDebt,
      monthlyIncome,
      purchasedPropertyAVM,
      salePrice,
      secondLienMonthlyPayment,
      closingCosts,
      availableFundsAfterDownpayment: availableFunds - downPaymentAmount,
      dtiAtMaxLoanPlusSecondLien: (monthlyDebt + secondLienMonthlyPayment) / monthlyIncome,
      dtiAtRequestedLoanAmount: parseFloat(app.decision.dti),
    };
  }

  render() {
    const { app, hurdle, onHide } = this.props;

    return (
      <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
        <table>
          <tbody>
            <tr>
              <th style={{ width: "60%" }}>Field</th>
              <th style={{ width: "40%" }}>Value</th>
            </tr>
            <tr>
              <td>Purchase price:</td>
              <td className="money">
                <Money value={this.state.salePrice} />
              </td>
            </tr>
            <tr>
              <td>Appraised value:</td>
              <td className="money">
                <Money value={this.state.appraisedValue} />
              </td>
            </tr>
            <tr>
              <td>AVM value:</td>
              <td className="money">
                <Money value={this.state.purchasedPropertyAVM} />
              </td>
            </tr>
            <tr>
              <td>Maximum loan amount (80% of appraised value):</td>
              <td className="money">
                <Money value={this.state.maxLoanForAppraisedValue} />
              </td>
            </tr>
            <tr>
              <td>
                Estimated downpayment (based on min downpayment (80% of appraised price), initial
                downpayment goal, and available funds):
              </td>
              <td className="money">
                <Money value={this.state.downPaymentAmount} />
              </td>
            </tr>
            <tr>
              <td>Available funds for downpayment:</td>
              <td className="money">
                <Money value={this.state.availableFunds} />
              </td>
            </tr>
            <tr>
              <td>Remaining available funds after downpayment:</td>
              <td className="money">
                <Money value={this.state.availableFundsAfterDownpayment} />
              </td>
            </tr>
            <tr>
              <td>
                Current DTI (assuming
                {parseFloat(this.props.app.decision.monthly_payment).toFixed(2)}
                monthly payment &amp; {this.props.app.computed_properties.loan_amount_base} total
                loan amount):
              </td>
              <td>{this.state.dtiAtRequestedLoanAmount.toFixed(4)}</td>
            </tr>
            <tr>
              <td>Big fix amount needed (purchase price - maximum loan value - downpayment):</td>
              <td className="money">
                <Money value={this.state.bigFixAmountNeeded} />
              </td>
            </tr>
            <tr>
              <td>Closing Costs:</td>
              <td className="money">
                <Money value={this.state.closingCosts} />
              </td>
            </tr>
            <tr>
              <td>Max approved second lien amount:</td>
              <td className="money">
                <Money value={this.state.maxSecondLienAmt} />
              </td>
            </tr>
            <tr>
              <td>Second lien monthly payment:</td>
              <td className="money">
                <Money value={this.state.secondLienMonthlyPayment} />
              </td>
            </tr>
            <tr>
              <td>DTI w/ 2nd lien:</td>
              <td>{this.state.dtiAtMaxLoanPlusSecondLien.toFixed(4)}</td>
            </tr>
            <tr>
              <td>FIXABLE:</td>
              <td>{this.state.fixable.toString()}</td>
            </tr>
          </tbody>
        </table>
      </HurdleView>
    );
  }
}

export default connect()(SecondLienHurdleEditor);
